import React from 'react';
import { translate } from 'react-i18next';

import ScheduleShiftLabel from 'components/ScheduleShiftLabel';
import { describeUnavailableChip } from 'components/ScheduleSidebar/unavailableRows.js';

// Most chips a day cell shows before collapsing into a "+N more", matching ScheduleDay.
const MAX_CHIPS = 3;

// One calendar date's bucket, as the view builds it.
export const emptyDay = () => ({
  shifts: [], roster: [], timeOff: [], naEntry: null,
});

const EMPTY_DAY = emptyDay();

// Held rows read as scheduled and carry a check. A row the API lists but would refuse is ghosted
// rather than rejected after the click; one it would accept with a warning is dashed.
function chipType(shift) {
  if (shift.held) return 'scheduled held';
  if (shift.blocked) return 'available ghosted';
  return shift.warned ? 'available warned' : 'available';
}

function SelfScheduleDay(props) {
  const {
    date, monthState, isToday, isInBlock, isSelected, day, onSelect, t,
  } = props;

  const key = date.format('YYYY-MM-DD');
  const select = () => onSelect(key);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select();
    }
  };

  let className = 'day';
  if (isToday) className += ' today';
  if (isSelected) className += ' selected';
  if (!isInBlock) className += ' outside-block';

  // Time off and the block's own N/A day are drawn as the schedule calendar draws them.
  let chips = day.shifts.map(shift => (
    <ScheduleShiftLabel key={`req-${shift.id}`} type={chipType(shift)} label={shift.label} />
  )).concat(day.roster.map(shift => (
    <ScheduleShiftLabel key={`shift-${shift.id}`} type="scheduled" label={shift.label} />
  ))).concat(day.timeOff.concat(day.naEntry || []).map(entry => (
    <ScheduleShiftLabel key={`off-${entry.id}`} {...describeUnavailableChip(entry, t)} />
  )));

  if (chips.length > MAX_CHIPS) {
    const moreLabel = `+${chips.length - MAX_CHIPS + 1} ${t('ScheduleDay.more')}`;
    chips = chips.slice(0, MAX_CHIPS - 1);
    chips.push(<ScheduleShiftLabel key="more" type="more" label={moreLabel} />);
  }

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      aria-label={Utils.formatLocaleDate(date)}
      onClick={select}
      onKeyDown={onKeyDown}
      data-date={date.date()}
      data-month={monthState}
      data-full-date={key}
    >
      {chips}
    </div>
  );
}

// Props: month (a moment), blockStart/blockEnd (dates), days (the view's per-date buckets),
// selectedDate and onSelectDate. Reuses the .month/.week/.day grid from _calendar.scss.
function SelfScheduleCalendar(props) {
  const {
    month, blockStart, blockEnd, days, selectedDate, onSelectDate, t,
  } = props;

  const monthStart = moment(month).startOf('month');
  const monthEnd = monthStart.clone().endOf('month');
  // Always Sunday-first, matching the header labels and the existing schedule calendar, rather
  // than startOf('week') which moves with the locale.
  const gridStart = monthStart.clone().subtract(monthStart.day(), 'days');
  const weekCount = Math.ceil((monthStart.day() + monthStart.daysInMonth()) / 7);
  const today = moment().startOf('day');
  const start = moment(blockStart);
  const end = moment(blockEnd);

  const weeks = [];
  for (let week = 0; week < weekCount; week += 1) {
    const cells = [];

    for (let weekday = 0; weekday < 7; weekday += 1) {
      const date = gridStart.clone().add((week * 7) + weekday, 'days');
      const key = date.format('YYYY-MM-DD');

      let monthState;
      if (date.isBefore(monthStart, 'day')) monthState = 'last';
      else if (date.isAfter(monthEnd, 'day')) monthState = 'next';

      cells.push(<SelfScheduleDay
        key={key}
        date={date}
        monthState={monthState}
        isToday={date.isSame(today, 'day')}
        isInBlock={date.isBetween(start, end, 'day', '[]')}
        isSelected={key === selectedDate}
        day={days[key] || EMPTY_DAY}
        onSelect={onSelectDate}
        t={t}
      />);
    }

    weeks.push(<div className="week" key={`week-${week}`}>{cells}</div>);
  }

  return (
    <article className="month">
      <header>
        {[0, 1, 2, 3, 4, 5, 6].map(day => (
          <div className="day-label" key={day}>{moment.weekdays(day)}</div>
        ))}
      </header>
      <div>{weeks}</div>
    </article>
  );
}

export default translate()(SelfScheduleCalendar);
